import { useCoursePermissions, useCourseTermContext } from '@components/context/CourseContext';
import { useCourseUnitsMenuContext } from '@components/layout/leftNav/menus/units/CourseUnitsMenu';
import { CoursePermissions } from '@components/types';
import { useQueryParams } from '@hooks/useQueryParams';
import { useMemo } from 'react';
import { useCourseActivitiesFixed, useCourseUnitsFixed } from './useCourseActivities';

/**
 * Returns the next item activity with all its info
 * Second parameter is the current ids for URL builder.
 * i.e current unitId, courseId, and acitvityId
 *
 * @returns
 */
export function useCourseNextActivity() {
    const { hasCoursePermission } = useCoursePermissions();
    const { data: units } = useCourseActivitiesFixed({
        variables: {
            includeHidden: false,
            includeDraft: hasCoursePermission(CoursePermissions.manage_course_content),
        },
    });
    const { unitId, activityId, id } = useQueryParams(['unitId', 'activityId', 'id']);

    const memoizedItems = useMemo(() => {
        const allOptions = units?.reduce((sum, u) => {
            u.activities.forEach((a) => {
                sum.push({ ...a, unit: u });
            });
            return sum;
        }, [] as any[]);
        return allOptions ?? [];
    }, [activityId, unitId, units]);

    const buttons = useMemo(() => {
        let leftItemLink: { [key: string]: any } | null = null;
        let rightItemLink: { [key: string]: any } | null = null;
        const currentItemIndex = memoizedItems.findIndex((a) => a.id === activityId);
        if (currentItemIndex > -1) {
            const rightIndex = Math.min(currentItemIndex + 1, memoizedItems.length);
            const leftIndex = Math.max(0, currentItemIndex - 1);
            if (currentItemIndex !== leftIndex) {
                leftItemLink = memoizedItems[leftIndex];
            }
            if (currentItemIndex !== rightIndex) {
                rightItemLink = memoizedItems[rightIndex];
            }
        }
        return { leftItemLink, rightItemLink };
    }, [memoizedItems, activityId, units]);

    return [buttons, { unitId, activityId, id }] as const;
}

export function useCourseNextUnit() {
    const state = useCourseUnitsMenuContext();
    const { data: units } = useCourseUnitsFixed({
        variables: {
            includeActivities: false,
        },
    });
    const { id } = useQueryParams(['unitId', 'activityId', 'id']);

    const buttons = useMemo(() => {
        let leftItemLink: { [key: string]: any } | null = null;
        let rightItemLink: { [key: string]: any } | null = null;
        if (!units) return { leftItemLink, rightItemLink };
        if (!state?.currentSelectedUnitId) return { leftItemLink, rightItemLink };
        const currentItemIndex =
            units.findIndex((a) => a.id === state?.currentSelectedUnitId) ?? -1;
        if (currentItemIndex > -1) {
            const rightIndex = Math.min(currentItemIndex + 1, units.length);
            const leftIndex = Math.max(0, currentItemIndex - 1);
            if (currentItemIndex !== leftIndex) {
                leftItemLink = units[leftIndex];
            }
            if (currentItemIndex !== rightIndex) {
                rightItemLink = units[rightIndex];
            }
        }
        return { leftItemLink, rightItemLink };
    }, [units, state?.currentSelectedUnitId]);

    return [buttons, { id }] as const;
}
